import { CompanyLogoField, type CompanyLogoFieldProps } from '@/components/company-logo-field';
import { NoyauPicker } from '@/components/noyau-picker';
import type { Company } from '@mss_creator/shared';
import { TextArea, TextField } from '@pms-digital/design-system';

/**
 * What the settings form edits and saves with « Enregistrer »: the company
 * without its logo, which the logo editor saves on its own (issue #20).
 */
export type CompanyInfoDraft = Omit<Company, 'logo' | 'logoUrl'>;

export interface CompanyInfoFormProps {
  company: CompanyInfoDraft;
  onChange: (patch: Partial<CompanyInfoDraft>) => void;
  /** The logo circle and what its editor does; the name is the company's. */
  logoField: Omit<CompanyLogoFieldProps, 'companyName'>;
}

/**
 * "Paramètres entreprise" — name/noyau/braavo/logo/contexte. Controlled:
 * every field change calls `onChange` with a partial patch; the parent page
 * (`CompanySpacePage`) holds the actual draft + dirty state and decides
 * when/what to save via the global save bar.
 *
 * `CompanyLogoField` is the one field outside the draft: its editor saves the
 * logo on the server as soon as it is confirmed (issue #20), so « Enregistrer »
 * has nothing to carry for it.
 *
 * Layout notes:
 * - The form runs the full width of the page. It was briefly capped at
 *   `max-w-3xl` on the theory that an uncapped 60% name field is absurdly
 *   wide on a large monitor — but in practice the cap left the whole section
 *   huddled on the left while the setup table below it ran edge to edge, and
 *   that read as broken rather than deliberate. Full width won. Do not
 *   reintroduce a cap here without also capping the sibling sections.
 * - Name/logo share a row at 60/40 (`3fr_2fr`), stacked below `md`. The logo
 *   column is 40% of the page holding an 80px circle, so the circle and its
 *   label are pushed to the column's right edge (`md:items-end`) rather than
 *   floating in the middle of that gap. That puts it flush right with braavo
 *   on the row below. Only from `md` up: once the row stacks, flush-right
 *   would leave the circle stranded opposite a left-aligned name field.
 * - Braavo sits on the noyau row, pushed right by `ml-auto` and capped at
 *   `max-w-xs`. At full page width that lands it at roughly the 20% the
 *   design asked for, while staying wide enough to actually type in —
 *   `braavoLevel` is 200 chars of free text that feeds the generation prompt.
 */
export function CompanyInfoForm({ company, onChange, logoField }: CompanyInfoFormProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[3fr_2fr]">
        <div>
          <TextField
            label="Nom"
            id="company-info-name"
            value={company.name}
            onChange={(event) => onChange({ name: event.target.value })}
          />
        </div>

        <div className="flex flex-col md:items-end">
          <span className="font-semibold text-small">Logo</span>
          <div className="mt-1">
            <CompanyLogoField companyName={company.name} {...logoField} />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
        <div>
          <span className="font-semibold text-small">Noyau</span>
          <div className="mt-1">
            <NoyauPicker value={company.noyau} onChange={(noyau) => onChange({ noyau })} />
          </div>
        </div>
        <div className="w-full sm:ml-auto sm:max-w-xs">
          <TextField
            label="Niveau Braavo"
            id="company-info-braavo"
            value={company.braavoLevel ?? ''}
            onChange={(event) => onChange({ braavoLevel: event.target.value || null })}
          />
        </div>
      </div>

      <div>
        <TextArea
          label="Contexte"
          id="company-info-contexte"
          value={company.contexte ?? ''}
          onChange={(event) => onChange({ contexte: event.target.value || null })}
        />
      </div>
    </div>
  );
}
